<template>
  <card-dialog
    v-model="modalVisible"
    fullscreen
    :title="$t('card_dialog.customize_screen.title')"
    persistent
    prevent-close
    @close="handleClose"
  >
    <v-card-text
      class="pa-0"
      style="background-color: #FCFCFC"
    >
      <v-row
        class="fill-height"
        no-gutters
      >
        <v-col class="fill-height overflow-auto px-6 pt-4">
          <v-row class="flex-column fill-height">
            <v-col
              cols="auto"
              class="pb-0"
            >
              <v-row
                align="center"
                no-gutters
              >
                <v-col cols="auto">
                  <validation-observer ref="validation">
                    <validation-provider
                      v-if="selectedLayout"
                      v-slot="{ errors }"
                      rules="required"
                    >
                      <v-text-field
                        v-model="selectedLayout.name"
                        :error-messages="errors"
                        :label="$t('text_input.layout_name')"
                        outlined
                        dense
                        style="width: 250px"
                      />
                    </validation-provider>
                  </validation-observer>
                </v-col>
                <v-spacer />
              </v-row>
            </v-col>
            <v-divider />
            <div style="height: 1px">
              <v-progress-linear
                v-if="submitting || deleting"
                indeterminate
                color="primary"
                :height="1"
              />
            </div>
            <v-col
              ref="layoutContainer"
              class="pa-0"
              style="flex-grow: 1; overflow-y: auto; scroll-behavior: smooth; background-color: #FCFCFC"
            >
              <div v-if="selectedLayout && selectedGrid.length">
                <grid-layout
                  :key="selectedLayout.id"
                  :layout="selectedGrid"
                  :col-num="12"
                  :row-height="70"
                  :is-draggable="true"
                  :is-resizable="true"
                  :is-bounded="true"
                  :vertical-compact="true"
                  :margin="[10, 10]"
                  :use-css-transforms="true"
                  @layout-updated="layoutUpdatedEvent()"
                >
                  <grid-item
                    v-for="item in selectedGrid"
                    :key="item.i"
                    :x="item.x"
                    :y="item.y"
                    :w="item.w"
                    :h="item.h"
                    :i="item.i"
                    :min-w="getMinimumWidth(item)"
                    :min-h="getMinimumHeight(item)"
                    :max-w="getMaximumWidth(item)"
                    :max-h="getMaximumHeight(item)"
                    style="touch-action: none; user-select: none; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none;"
                  >
                    <div
                      v-if="!layoutReady || variables.loading || charts.loading"
                      class="fill-height d-flex justify-center align-center"
                    >
                      <v-progress-circular
                        indeterminate
                        size="75"
                        color="primary"
                        class="py-10"
                      />
                    </div>
                    <div
                      v-else
                      class="fill-height"
                    >
                      <div
                        v-if="item.type === 'card'"
                        class="fill-height"
                      >
                        <v-card class="rounded-lg fill-height">
                          <v-row
                            no-gutters
                            class="pa-4 fill-height"
                            align="center"
                          >
                            <v-col>
                              <div class="subtitle-2 grey--text font-weight-regular fill-height d-flex justify-space-between align-center">
                                <div style="max-width: 250px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                                  {{ getVariableName(item.key) }}
                                </div>
                                <v-btn
                                  icon
                                  @click="removeItem(item)"
                                >
                                  <v-icon small>
                                    fal fa-times
                                  </v-icon>
                                </v-btn>
                              </div>
                              <div v-if="item.h > 1">
                                <div
                                  v-if="player.player_stats[item.key]"
                                  class="text-h4 mt-2 grey--text text--darken-2"
                                >
                                  {{ player.player_stats[item.key] }}
                                </div>
                                <div
                                  v-else
                                  class="text-h4 mt-2 grey--text"
                                >
                                  N/A
                                </div>
                                <div class="mt-4">
                                  <v-progress-linear
                                    :height="8"
                                    rounded
                                    background-color="grey"
                                    :background-opacity=".2"
                                    :color="getColorForValue(player.player_stats[item.key], player.player_stats[item.key] * 1.2)"
                                    :value="85"
                                  />
                                </div>
                              </div>
                            </v-col>
                          </v-row>
                        </v-card>
                      </div>
                      <div
                        v-else-if="item.type === 'table'"
                        class="fill-height"
                      >
                        <v-card class="rounded-lg fill-height">
                          <v-card-title class="d-flex justify-space-between">
                            {{ $t('card_title.table') }}
                            <v-btn
                              icon
                              @click="removeItem(item)"
                            >
                              <v-icon small>
                                fal fa-times
                              </v-icon>
                            </v-btn>
                          </v-card-title>
                          <v-card-text class="pa-0">
                            <v-data-table
                              :headers="defaultTableHeaders"
                              :items="chartPlayers"
                              hide-default-footer
                              dense
                              disable-pagination
                              disable-sort
                              disable-filtering
                            >
                              <template v-slot:item="{ item: tablePlayer }">
                                <tr>
                                  <td class="text-no-wrap">
                                    {{ tablePlayer.first_name }} {{ tablePlayer.last_name }}
                                  </td>
                                  <td
                                    v-for="tableHeader in defaultTableHeaders.slice(1)"
                                    :key="tableHeader.id"
                                  >
                                    <table-bar-value
                                      :value="tablePlayer.player_stats[tableHeader.value]"
                                      :max="tablePlayer.player_stats[tableHeader.value] * 1.1"
                                      :color="getColorForValue(tablePlayer.player_stats[tableHeader.value], tablePlayer.player_stats[tableHeader.value] * 1.1)"
                                    >
                                      {{ tablePlayer.player_stats[tableHeader.value] }}
                                    </table-bar-value>
                                  </td>
                                </tr>
                              </template>
                            </v-data-table>
                          </v-card-text>
                        </v-card>
                      </div>
                      <div
                        v-else-if="item.type === 'chart'"
                        class="fill-height"
                      >
                        <diagnostic-chart
                          :chart="item"
                          :stats="chartPlayers"
                          :player="player.data"
                          :height="item.h * 70 + 50"
                          :removable="true"
                          style="pointer-events: none"
                          @remove="removeItem(item)"
                        />
                        <team-load-chart
                          :chart="item"
                          :players="chartPlayers"
                          :removable="true"
                          style="pointer-events: none"
                          @remove="removeItem(item)"
                        />
                        <team-wellness-chart
                          :chart="item"
                          :team-stats="chartPlayers"
                          :removable="true"
                          style="pointer-events: none"
                          @remove="removeItem(item)"
                        />
                        <load-chart-individual
                          :chart="item"
                          :stats="chartPlayers"
                          :removable="true"
                          style="pointer-events: none"
                          @remove="removeItem(item)"
                        />
                        <performance-graph-individual
                          :variables="variables.data"
                          :chart="item"
                          :stats="player.player_stats"
                          :removable="true"
                          :event="eventTemplate"
                          :height="String(item.h * 70 - 50)"
                          style="pointer-events: none"
                          @remove="removeItem(item)"
                        />
                        <performance-graph
                          class="fill-height"
                          :players="chartPlayers"
                          :chart="item"
                          :stats="player.player_stats"
                          :removable="true"
                          style="pointer-events: none"
                          @remove="removeItem(item)"
                        />
                      </div>
                      <div
                        v-else-if="item.type === 'line_chart'"
                        class="fill-height"
                      >
                        <performance-trend-chart
                          :variable-key="item.key"
                          :events="chartPlayers"
                          :title="getVariableName(item.key)"
                          style="pointer-events: none"
                          :removable="true"
                          @remove="removeItem(item)"
                        />
                      </div>
                      <div
                        v-else-if="item.type === 'bar_chart'"
                        class="fill-height"
                      >
                        <performance-bar-chart
                          :variable-key="item.key"
                          :players="chartPlayers"
                          :removable="true"
                          @remove="removeItem(item)"
                        />
                      </div>
                      <div
                        v-else-if="item.type === 'diagnostic_table'"
                        class="fill-height"
                      >
                        <v-card class="rounded-lg fill-height">
                          <v-card-title class="d-flex justify-space-between">
                            {{ $t("card_title.participant_comparison") }}
                            <v-btn
                              icon
                              @click="removeItem(item)"
                            >
                              <v-icon small>
                                fal fa-times
                              </v-icon>
                            </v-btn>
                          </v-card-title>
                          <v-card-text class="pa-0">
                            <v-data-table
                              :headers="defaultDiagnosticsTableHeader"
                              :items="chartPlayers"
                              hide-default-footer
                              dense
                              disable-pagination
                              disable-sort
                              disable-filtering
                            >
                              <template v-slot:item="{ item: tablePlayer }">
                                <tr>
                                  <td class="text-no-wrap">
                                    {{ tablePlayer.first_name }} {{ tablePlayer.last_name }}
                                  </td>
                                  <td
                                    v-for="tableHeader in defaultDiagnosticsTableHeader.slice(1)"
                                    :key="tableHeader.id"
                                  >
                                    <table-bar-value
                                      :value="tablePlayer.player_stats[tableHeader.value]"
                                      :max="tablePlayer.player_stats[tableHeader.value] * 1.1"
                                      :color="getColorForValue(tablePlayer.player_stats[tableHeader.value], tablePlayer.player_stats[tableHeader.value] * 1.1)"
                                    >
                                      {{ tablePlayer.player_stats[tableHeader.value] }}
                                    </table-bar-value>
                                  </td>
                                </tr>
                              </template>
                            </v-data-table>
                          </v-card-text>
                        </v-card>
                      </div>
                    </div>
                  </grid-item>
                </grid-layout>
              </div>
              <div
                v-else
                class="text-center pa-10"
              >
                {{ $t("layouts.add_items_to_start") }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-divider vertical />
        <v-col
          cols="3"
          class="fill-height overflow-auto"
          style="flex-grow: 1; overflow-y: auto;"
        >
          <div
            class="fill-height pa-6"
            style="flex-grow: 1; overflow-y: auto;"
          >
            <subtitle class="mb-2">
              {{ $t("layouts.items.tables") }}
            </subtitle>
            <div v-if="dashboardConfiguration.table">
              {{ $t("layouts.items.tables.generalTable") }}
            </div>
            <v-card
              v-if="dashboardConfiguration.table"
              class="rounded-lg mt-3"
              :disabled="layoutContainsPrimaryElement"
              @click="addTableToLayout('table')"
            >
              <v-card-text>
                <v-img
                  :src="tableImg"
                  height="200"
                  contain
                />
              </v-card-text>
            </v-card>
            <div
              v-if="dashboardConfiguration.readiness_table"
              class="mt-5"
            >
              <div>
                {{ $t("layouts.items.tables.diagnosticsTable") }}
              </div>
              <v-card
                class="rounded-lg mt-3"
                @click="addTableToLayout('diagnostic_table')"
              >
                <v-card-text>
                  <v-img
                    :src="diagnosticTableImg"
                    height="125"
                    contain
                  />
                </v-card-text>
              </v-card>
            </div>
            <subtitle class="mt-5 mb-2">
              {{ $t("layouts.items.cards") }}
            </subtitle>
            <v-card
              class="rounded-lg"
              height="150"
              @click="selectVariableModal = true; itemToAdd = 'card'"
            >
              <v-row
                no-gutters
                class="pa-4 fill-height elevation-8"
                align="center"
              >
                <v-col>
                  <div class="subtitle-2 grey--text font-weight-regular fill-height d-flex justify-space-between align-center">
                    {{ $t("layouts.items.cards.variableName") }}
                  </div>
                  <div>
                    <div class="text-h4 mt-2 grey--text text--darken-2">
                      800
                    </div>
                    <div class="mt-4">
                      <v-progress-linear
                        :height="8"
                        rounded
                        background-color="grey"
                        :background-opacity=".2"
                        :color="getColorForValue(80, 100)"
                        :value="80"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card>

            <subtitle class="mt-5 mb-2">
              {{ $t("layouts.items.charts") }}
            </subtitle>
            <v-row
              v-if="dashboardConfiguration && dashboardConfiguration.line_charts"
              class="d-flex flex-column"
            >
              <v-col>
                {{ $t("layouts.items.charts.line_chart") }}
              </v-col>
              <v-col>
                <v-card class="d-flex justify-center">
                  <v-img
                    class="py-2 elevation-8 rounded-lg"
                    style="cursor: pointer"
                    :src="line_chart"
                    :height="175"
                    aspect-ratio="1"
                    contain
                    @click="selectVariableModal = true; itemToAdd = 'line_chart'"
                  />
                </v-card>
              </v-col>
            </v-row>
            <v-divider
              v-if="dashboardConfiguration && dashboardConfiguration.line_charts"
              class="my-5"
            />
            <v-row
              v-if="dashboardConfiguration && dashboardConfiguration.bar_charts"
              class="d-flex flex-column"
            >
              <v-col>
                {{ $t("layouts.items.charts.bar_chart") }}
              </v-col>
              <v-col>
                <v-card class="d-flex justify-center rounded-lg">
                  <v-img
                    class="py-2 elevation-8"
                    style="cursor: pointer"
                    :src="bar_chart"
                    :height="175"
                    aspect-ratio="1"
                    contain
                    @click="selectVariableModal = true; itemToAdd = 'bar_chart'"
                  />
                </v-card>
              </v-col>
            </v-row>
            <v-divider
              v-if="dashboardConfiguration && dashboardConfiguration.bar_charts"
              class="mt-5"
            />
            <v-col
              v-for="(chart, index) in filteredCharts"
              :key="chart.id"
              class="pa-0 mt-5"
            >
              <div>
                <v-divider
                  v-if="index > 0"
                  class="my-5"
                />
                <v-row class="d-flex flex-column">
                  <v-col>
                    {{ $t(`charts.${chart.key}.title`) }}
                  </v-col>
                  <v-col>
                    <v-card class="d-flex justify-center rounded-lg">
                      <v-img
                        class="py-2 elevation-8 rounded-lg"
                        style="cursor: pointer"
                        :src="chart.image_url"
                        :height="175"
                        aspect-ratio="1"
                        contain
                        @click="handleChartClick(chart)"
                      />
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          color="primary"
          class="text-none"
          :disabled="!selectedLayout"
          :loading="submitting || deleting"
          @click="submit"
        >
          {{ $t('button.save') }}
        </v-btn>
        <v-btn
          color="grey"
          text
          @click="modalVisible = false"
        >
          {{ $t('button.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="selectedLayout && selectedLayout.id"
          color="error"
          text
          :disabled="layouts.length === 1"
          @click="deleteConfirmationDialogVisible = true"
        >
          <v-icon
            left
            x-small
          >
            far fa-trash
          </v-icon>
          {{ $t('button.delete') }}
        </v-btn>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteLayout"
    />

    <card-dialog
      v-model="selectVariableModal"
      :title="$t('card_dialog.select_variable.title')"
      max-width="600"
      persistent
      prevent-close
      @close="selectVariableModal = false; itemToAdd = null; chartKey = null"
    >
      <template v-slot:title>
        <v-card-title
          class="blue-grey lighten-5 px-6 py-4"
        >
          <v-row dense>
            <v-col>
              {{ $t('card_dialog.select_variable.title') }}
            </v-col>
          </v-row>
          <v-row
            dense
            class="mt-3"
          >
            <v-col>
              <v-text-field
                v-model="filters.search"
                append-icon="fa-search"
                solo
                dense
                clearable
                hide-details
                :placeholder="$t('text_input.filter_by_name')"
              />
            </v-col>
            <v-col>
              <v-select
                v-model="filters.category"
                :items="filteredCategories"
                item-value="name"
                item-text="name"
                dense
                solo
                clearable
                hide-details
                :placeholder="$t('text_input.filter_by_group')"
              />
            </v-col>
          </v-row>
        </v-card-title>
      </template>
      <v-card-text>
        <div
          v-if="variables.loading"
          class="d-flex justify-center pa-8"
        >
          <v-progress-circular
            indeterminate
            size="100"
            width="8"
            color="grey lighten-2"
          />
        </div>
        <v-list v-else>
          <template v-for="variable in searchFilteredVariables">
            <v-list-item
              v-if="!dashboardConfiguration.selectAggregation || itemToAdd === 'chart' || variable.category === 'WELLNESS'"
              :key="variable.value"
              @click="addItemToLayout(variable)"
            >
              <v-list-item-title class="font-weight-bold">
                {{ variable.long_text }}
              </v-list-item-title>
            </v-list-item>
            <v-list-group
              v-else
              :key="variable.value"
              :value="false"
              no-action
            >
              <template v-slot:activator>
                <v-list-item-title class="font-weight-bold">
                  {{ variable.long_text }}
                </v-list-item-title>
              </template>
              <div class="d-flex justify-center pa-2">
                <v-btn-toggle>
                  <v-btn
                    v-for="agg in aggregations"
                    :key="agg.value"
                    small
                    outlined
                    color="primary"
                    @click="addItemToLayout({ ...variable, value: variable.value + '_' + agg.value, aggregation: agg.value })"
                  >
                    {{ agg.value }}
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-list-group>
          </template>
        </v-list>
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-model="selectAggregationModal"
      :title="$t('card_dialog.select_aggregation.title')"
      max-width="600"
      persistent
      prevent-close
      @close="selectAggregationModal = false; itemToAdd = null; chartKey = null"
    >
      <v-card-text class="pa-4">
        <v-list>
          <v-list-item
            v-for="agg in aggregations"
            :key="agg.value"
            @click="addItemToLayout({ aggregation: agg.value })"
          >
            {{ agg.text }}
          </v-list-item>
        </v-list>
      </v-card-text>
    </card-dialog>
    <delete-confirmation-dialog
      :visible.sync="closeConfirmationDialogVisible"
      :title="$t('card_dialog.lose_progress.title')"
      :text="$t('card_dialog.lose_progress.label')"
      :cta-text="$t('card_dialog.lose_progress.button')"
      @confirmed="modalVisible = false"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import { GridLayout, GridItem } from 'vue-grid-layout'
import { LayoutApiService } from '@/data/services/LayoutApiService'
import { EventBus } from '@/plugins/bus'
import tableImg from '@/assets/img/performance/table.png'
import diagnosticTableImg from '@/assets/img/performance/diagnostic_table.png'
import line_chart from '@/assets/img/performance/line_chart.png'
import bar_chart from '@/assets/img/performance/bar_chart.png'
import { ChartApiService } from '@/data/services/ChartApiService'
import i18n from '@/plugins/i18n'
import { getColorForValue, getVariableNameWithAggregation } from '@/utils/helpers'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import TableBarValue from '@/components/helpers/TableBarValue.vue'
import { cloneDeep } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle.vue'
import PerformanceGraph from '@/components/performance/PerformanceGraph.vue'
import PerformanceTrendChart from '@/components/charts/implementation/PerformanceTrendChart.vue'
import PerformanceGraphIndividual from '@/components/performance/PerformanceGraphIndividual.vue'
import LoadChartIndividual from '@/components/training-load/LoadChartIndividual.vue'
import { dashboardConfig } from '@/data/models/DashboardConfig'
import PerformanceBarChart from '@/components/charts/implementation/PerformanceBarChart.vue'
import TeamLoadChart from '@/components/training-load/TeamLoadChart.vue'
import TeamWellnessChart from '@/components/training-load/TeamWellnessChart.vue'
import DiagnosticChart from '@/components/charts/implementation/DiagnosticChart.vue'

const players = [
  {
    first_name: i18n.t('player.label.title'),
    last_name: 'Ultrax',
    start_at: '2023-01-01',
    player_stats: { CACGCSMXV: 25.51, CACTCSVB1TD: 3014.2, CACTCSVB2TD: 2633.21, CACTCSVB3TD: 586.98, CALETLDLHSR: 166.06, CALETLDLHSR_G: 2.59, CALETLDLHSR_GALL: 8786.24, CALETLDLSP: 1.97, CALETLDLSP_GALL: 13, CALETLDMP: 13, CAPGCSACB13EG2: 4, CAPGCSDCB13EG2: 2, CAPGCSMXAC: 5.03, CAPGCSMXDC: -4.45, UXACB58: 148, UXACB68: 29, UXACCALL: 3663, UXAERAVGMP: 13, UXANAENRG: 13, UXANAINDX: 13, UXAVGVEL: 5.59, UXDCB13: 18, UXDCB14: 114, UXDECALL: 3689, UXENGEXP: 13, UXEQDIST: 13, UXEQDISTINDX: 13, UXHMLD: 13, UXHSRmin: 2.37, UXINTS: 8, UXLSPmin: 0.03, UXMAXVELREF: 64.42, UXMPEAVGDR: 13, UXMPERECAVGT: 13, UXMPETDR: 13, UXMPTOTAL: 13, UXNUMMPE: 13, UXPCSEEF: 6, UXPCSMYPM: 90.17, UXPCSTD: 6402.43, UXPCSTDR: 70.63, UXPCSTD_GALL: 175.82, UXPCSTPL: 13, UXVELALL: 13, UXVELB17: 237, UXVELB27: 58, UXVELB37: 7, UXVELB47: 1, UXVELB57: 0, UXVELB67: 0, UXVOL: 6, injured: false, TG: 8, TL: 480, TS: 0, TT: 0, TTM: 60, ENJY: 4, NN50: 760.1, RMSSD: 59.4, SDANN_2: 104.883, SDANN_5: 120.791, SDNN: 102.3, SDNN_index2: 48.224, SDNN_index5: 61.6, SDSD: 59.4, lnRMSSD: 4.389, lnRMSSDcv: 1.463, pNN50: 4.817, AE: 13, ANAE: 13, BMP: 158.907, CE: 13, HRAVG: 158.995, HRMAX: 210.2, HRR: 0.616, L3DHRR: 13, TRIMP: 244.794, TRIMPMIN: 2.073, ZONE1DR: 4.256, ZONE2DR: 3.057, ZONE3DR: 3.135, ZONE4DR: 1.606, ZONE5DR: 0.044, BCPBCPBMI: 20.56, MASVNBISONAFI: 435, MASVNBISONAI: 8, TG_dist: [{ grade: 3, value: 16.67 }, { grade: 4, value: 16.67 }, { grade: 5.5, value: 8.33 }, { grade: 6, value: 8.33 }, { grade: 7, value: 8.33 }, { grade: 8.5, value: 16.67 }, { grade: 9, value: 16.67 }, { grade: 10, value: 8.33 }], ENJY_dist: [{ grade: 2, value: 20 }, { grade: 3, value: 40 }, { grade: 4, value: 30 }, { grade: 5, value: 10 }] }
  },
  {
    first_name: i18n.t('player.label.title'),
    last_name: 2,
    start_at: '2023-02-01',
    player_stats: { CACGCSMXV: 10.5, CACTCSVB1TD: 2000.5, CACTCSVB2TD: 3000.5, CACTCSVB3TD: 1000.5, CALETLDLHSR: 200, CALETLDLHSR_G: 2.5, CALETLDLHSR_GALL: 500.23, CALETLDLSP: 100.5, CALETLDLSP_GALL: 2000.31, CALETLDMP: 50.39, CAPGCSACB13EG2: 1, CAPGCSDCB13EG2: 2, CAPGCSMXAC: 2.04, CAPGCSMXDC: -2.95, UXACB58: 50, UXACB68: 5, UXACCALL: 200, UXAERAVGMP: null, UXANAENRG: null, UXANAINDX: null, UXAVGVEL: 2.28, UXDCB13: 9, UXDCB14: 50, UXDECALL: 200, UXENGEXP: null, UXEQDIST: null, UXEQDISTINDX: null, UXHMLD: 2000.6, UXHSRmin: 2.51, UXINTS: 400, UXLSPmin: 0.67, UXMAXVELREF: 50, UXMPEAVGDR: 15.81, UXMPERECAVGT: 9.38, UXMPETDR: 2000.8, UXMPTOTAL: 5000000, UXNUMMPE: 64, UXPCSEEF: 15, UXPCSMYPM: 34.35, UXPCSTD: 4000.4, UXPCSTDR: 55.17, UXPCSTD_GALL: 100.92, UXPCSTPL: null, UXVELALL: null, UXVELB17: 150, UXVELB27: 50, UXVELB37: 7, UXVELB47: 4, UXVELB57: 1, UXVELB67: 0, UXVOL: 50, injured: false, NN50: 345, RMSSD: 26.99, SDANN_2: 47.32, SDANN_5: 54.8, SDNN: 46.9, SDNN_index2: 21.84, SDNN_index5: 28, SDSD: 26.99, lnRMSSD: 2.99, lnRMSSDcv: 0.32, pNN50: 2.37, AE: null, ANAE: null, BMP: 72.23, CE: null, HRAVG: 72.54, HRMAX: 95, HRR: 0.25, L3DHRR: null, TRIMP: 111.26, TRIMPMIN: 0.93, ZONE1DR: 1.93, ZONE2DR: 1.38, ZONE3DR: 1.42, ZONE4DR: 0.73, ZONE5DR: 0.02, TG: 4, TL: 500, TS: 0, TT: 0, TTM: 120, ENJY: 3, BCPBCPBMI: 21.53, MASVNBISONAFI: 346, MASVNBISONAI: 4 }
    },
  {
    first_name: i18n.t('player.label.title'),
    last_name: 3,
    start_at: '2023-03-01',
    player_stats: { CACGCSMXV: 34.023, CACTCSVB1TD: 3168, CACTCSVB2TD: 3716.9, CACTCSVB3TD: 1153.9, CALETLDLHSR: 469.7, CALETLDLHSR_G: 6.182, CALETLDLHSR_GALL: 1048.564, CALETLDLSP: 204.6, CALETLDLSP_GALL: 2879.152, CALETLDMP: 116.04, CAPGCSACB13EG2: 2.211, CAPGCSDCB13EG2: 3.311, CAPGCSMXAC: 4.454, CAPGCSMXDC: -4.434, UXACB58: 85.811, UXACB68: 12.111, UXACCALL: 448.8, UXAERAVGMP: null, UXANAENRG: null, UXANAINDX: null, UXAVGVEL: 4.709, UXDCB13: 19.811, UXDCB14: 102.311, UXDECALL: 469.8, UXENGEXP: null, UXEQDIST: null, UXEQDISTINDX: null, UXHMLD: 3934.26, UXHSRmin: 6.061, UXINTS: 788.7, UXLSPmin: 1.837, UXMAXVELREF: 110.011, UXMPEAVGDR: 34.991, UXMPERECAVGT: 21.329, UXMPETDR: 4479.8, UXMPTOTAL: 7735698.3, UXNUMMPE: 140.81, UXPCSEEF: 33.011, UXPCSMYPM: 75.196, UXPCSTD: 8346.25, UXPCSTDR: 121.198, UXPCSTD_GALL: 186.923, UXPCSTPL: null, UXVELALL: null, UXVELB17: 344.3, UXVELB27: 139.7, UXVELB37: 18.7, UXVELB47: 10.9, UXVELB57: 3.3, UXVELB67: 1.1, UXVOL: 110.011, injured: false, NN50: 760.1, RMSSD: 59.4, SDANN_2: 104.883, SDANN_5: 120.791, SDNN: 102.3, SDNN_index2: 48.224, SDNN_index5: 61.6, SDSD: 59.4, lnRMSSD: 4.389, lnRMSSDcv: 1.463, pNN50: 4.817, AE: null, ANAE: null, BMP: 158.907, CE: null, HRAVG: 158.995, HRMAX: 210.2, HRR: 0.616, L3DHRR: null, TRIMP: 244.794, TRIMPMIN: 2.073, ZONE1DR: 4.256, ZONE2DR: 3.057, ZONE3DR: 3.135, ZONE4DR: 1.606, ZONE5DR: 0.044, TG: 5, TL: 600, TS: 0, TT: 0, TTM: 120, ENJY: 2, BCPBCPBMI: 18.53, MASVNBISONAFI: 476, MASVNBISONAI: 5 }
    },
  {
    first_name: i18n.t('player.label.title'),
    last_name: 4,
    start_at: '2023-04-01',
    player_stats: { CACGCSMXV: 32.47, CACTCSVB1TD: 3023.895, CACTCSVB2TD: 3547.845, CACTCSVB3TD: 1101.24, CALETLDLHSR: 447.3, CALETLDLHSR_G: 5.889, CALETLDLHSR_GALL: 1000.99415, CALETLDLSP: 195.195, CALETLDLSP_GALL: 2749.22255, CALETLDMP: 111.71, CAPGCSACB13EG2: 2.1, CAPGCSDCB13EG2: 3.15, CAPGCSMXAC: 4.244, CAPGCSMXDC: -5.2025, UXACB58: 81.9, UXACB68: 11.55, UXACCALL: 427.35, UXAERAVGMP: null, UXANAENRG: null, UXANAINDX: null, UXAVGVEL: 4.494, UXDCB13: 18.9, UXDCB14: 97.65, UXDECALL: 448.35, UXENGEXP: null, UXEQDIST: null, UXEQDISTINDX: null, UXHMLD: 3755.43, UXHSRmin: 5.7855, UXINTS: 752.85, UXLSPmin: 1.7535, UXMAXVELREF: 105, UXMPEAVGDR: 33.3975, UXMPERECAVGT: 20.307, UXMPETDR: 4275.39, UXMPTOTAL: 7384075.15, UXNUMMPE: 134.4, UXPCSEEF: 31.5, UXPCSMYPM: 71.7275, UXPCSTD: 7966.77, UXPCSTDR: 115.675, UXPCSTD_GALL: 178.416, UXPCSTPL: null, UXVELALL: null, UXVELB17: 328.65, UXVELB27: 133.35, UXVELB37: 18.585, UXVELB47: 9.45, UXVELB57: 2.1, UXVELB67: 0, UXVOL: 105, injured: false, NN50: 724.5, RMSSD: 56.69, SDANN_2: 100.092, SDANN_5: 115.29, SDNN: 97.545, SDNN_index2: 46.038, SDNN_index5: 58.8, SDSD: 56.69, lnRMSSD: 4.1895, lnRMSSDcv: 1.386, pNN50: 4.6085, AE: null, ANAE: null, BMP: 151.688, CE: null, HRAVG: 151.767, HRMAX: 200.55, HRR: 0.5775, L3DHRR: null, TRIMP: 233.805, TRIMPMIN: 1.9635, ZONE1DR: 4.053, ZONE2DR: 2.9135, ZONE3DR: 2.978, ZONE4DR: 1.533, ZONE5DR: 0.042, TG: 7, TL: 800, TS: 0, TT: 0, TTM: 120, ENJY: 4, BCPBCPBMI: 19.06, MASVNBISONAFI: 453, MASVNBISONAI: 4 }
    },
  {
    first_name: i18n.t('player.label.title'),
    last_name: 5,
    start_at: '2023-05-01',
    player_stats: { CACGCSMXV: 30.92, CACTCSVB1TD: 2879.9, CACTCSVB2TD: 3378.9, CACTCSVB3TD: 1048.8, CALETLDLHSR: 426, CALETLDLHSR_G: 5.61, CALETLDLHSR_GALL: 953.23, CALETLDLSP: 185.9, CALETLDLSP_GALL: 2618.31, CALETLDMP: 106.39, CAPGCSACB13EG2: 2, CAPGCSDCB13EG2: 3, CAPGCSMXAC: 4.04, CAPGCSMXDC: -4.95, UXACB58: 78, UXACB68: 11, UXACCALL: 407, UXAERAVGMP: null, UXANAENRG: null, UXANAINDX: null, UXAVGVEL: 4.28, UXDCB13: 18, UXDCB14: 93, UXDECALL: 427, UXENGEXP: null, UXEQDIST: null, UXEQDISTINDX: null, UXHMLD: 3576.6, UXHSRmin: 5.51, UXINTS: 717, UXLSPmin: 1.67, UXMAXVELREF: 100, UXMPEAVGDR: 31.81, UXMPERECAVGT: 19.38, UXMPETDR: 4071.8, UXMPTOTAL: 7032453, UXNUMMPE: 128, UXPCSEEF: 30, UXPCSMYPM: 68.35, UXPCSTD: 7587.4, UXPCSTDR: 110.17, UXPCSTD_GALL: 169.92, UXPCSTPL: null, UXVELALL: null, UXVELB17: 313, UXVELB27: 127, UXVELB37: 17, UXVELB47: 9, UXVELB57: 2, UXVELB67: 0, UXVOL: 100, injured: false, NN50: 690, RMSSD: 53.99, SDANN_2: 95.32, SDANN_5: 109.8, SDNN: 92.9, SDNN_index2: 43.84, SDNN_index5: 56, SDSD: 53.99, lnRMSSD: 3.99, lnRMSSDcv: 1.32, pNN50: 4.37, AE: null, ANAE: null, BMP: 144.46, CE: null, HRAVG: 144.54, HRMAX: 191, HRR: 0.55, L3DHRR: null, TRIMP: 222.53, TRIMPMIN: 1.87, ZONE1DR: 3.86, ZONE2DR: 2.77, ZONE3DR: 2.84, ZONE4DR: 1.46, ZONE5DR: 0.04, TG: 9, TL: 1000, TS: 0, TT: 0, TTM: 120, ENJY: 3, BCPBCPBMI: 25.56, MASVNBISONAFI: 445, MASVNBISONAI: 8 }
  },
  {
    first_name: i18n.t('player.label.title'),
    last_name: 6,
    start_at: '2023-06-01',
    player_stats: { CACGCSMXV: 34.023, CACTCSVB1TD: 3168, CACTCSVB2TD: 3716.9, CACTCSVB3TD: 1153.9, CALETLDLHSR: 469.7, CALETLDLHSR_G: 6.182, CALETLDLHSR_GALL: 1048.564, CALETLDLSP: 204.6, CALETLDLSP_GALL: 2879.152, CALETLDMP: 116.04, CAPGCSACB13EG2: 2.211, CAPGCSDCB13EG2: 3.311, CAPGCSMXAC: 4.454, CAPGCSMXDC: -4.434, UXACB58: 85.811, UXACB68: 12.111, UXACCALL: 448.8, UXAERAVGMP: null, UXANAENRG: null, UXANAINDX: null, UXAVGVEL: 4.709, UXDCB13: 19.811, UXDCB14: 102.311, UXDECALL: 469.8, UXENGEXP: null, UXEQDIST: null, UXEQDISTINDX: null, UXHMLD: 3934.26, UXHSRmin: 6.061, UXINTS: 788.7, UXLSPmin: 1.837, UXMAXVELREF: 110.011, UXMPEAVGDR: 34.991, UXMPERECAVGT: 21.329, UXMPETDR: 4479.8, UXMPTOTAL: 7735698.3, UXNUMMPE: 140.81, UXPCSEEF: 33.011, UXPCSMYPM: 75.196, UXPCSTD: 8346.25, UXPCSTDR: 121.198, UXPCSTD_GALL: 186.923, UXPCSTPL: null, UXVELALL: null, UXVELB17: 344.3, UXVELB27: 139.7, UXVELB37: 18.7, UXVELB47: 10.9, UXVELB57: 3.3, UXVELB67: 1.1, UXVOL: 110.011, injured: false, NN50: 760.1, RMSSD: 59.4, SDANN_2: 104.883, SDANN_5: 120.791, SDNN: 102.3, SDNN_index2: 48.224, SDNN_index5: 61.6, SDSD: 59.4, lnRMSSD: 4.389, lnRMSSDcv: 1.463, pNN50: 4.817, AE: null, ANAE: null, BMP: 158.907, CE: null, HRAVG: 158.995, HRMAX: 210.2, HRR: 0.616, L3DHRR: null, TRIMP: 244.794, TRIMPMIN: 2.073, ZONE1DR: 4.256, ZONE2DR: 3.057, ZONE3DR: 3.135, ZONE4DR: 1.606, ZONE5DR: 0.044, TG: 8, TL: 800, TS: 0, TT: 0, TTM: 120, ENJY: 4, BCPBCPBMI: 23.34, MASVNBISONAFI: 512, MASVNBISONAI: 9 }
  }
]

const defaultTableHeaders = [
  { text: i18n.t('table_headers.name'), value: 'last_name' },
  { text: i18n.t('variables.UXPCSTDR'), value: 'UXPCSTDR' },
  { text: i18n.t('variables.UXPCSTD'), value: 'UXPCSTD' },
  { text: i18n.t('variables.CALETLDLHSR'), value: 'CALETLDLHSR' },
  { text: i18n.t('variables.CALETLDLSP'), value: 'CALETLDLSP' },
  { text: i18n.t('variables.UXPCSMYPM'), value: 'UXPCSMYPM' },
  { text: i18n.t('variables.CACGCSMXV'), value: 'CACGCSMXV' },
  { text: i18n.t('variables.CAPGCSACB13EG2'), value: 'CAPGCSACB13EG2' },
  { text: i18n.t('variables.CAPGCSDCB13EG2'), value: 'CAPGCSDCB13EG2' }
]

const defaultDiagnosticsTableHeader = [
  { text: i18n.t('table_headers.name'), value: 'last_name' },
  { text: 'Body composition: BMI', value: 'BCPBCPBMI' },
  { text: 'VALD NordBord Nordic: Average force imbalance', value: 'MASVNBISONAFI' },
  { text: 'VALD NordBord Nordic: Impulse imbalance', value: 'MASVNBISONAI' }
]

const eventTemplate = {
  data: {
    start_at: '2025-01-01T09:00:00Z',
    end_at: '2025-01-01T10:00:00Z'
  }
}

export default {
  name: 'LayoutsFormDialog',
  components: {
    DiagnosticChart,
    TeamLoadChart,
    TeamWellnessChart,
    PerformanceBarChart,
    LoadChartIndividual,
    PerformanceGraphIndividual,
    PerformanceTrendChart,
    PerformanceGraph,
    Subtitle,
    TableBarValue,
    DeleteConfirmationDialog,
    CardDialog,
    GridLayout,
    GridItem
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      variables: {
        data: [],
        loading: false
      },
      charts: {
        data: [],
        loading: false
      },
      selectedLayout: null,
      selectedGrid: null,
      submitting: false,
      deleting: false,
      menuVisible: false,
      deleteConfirmationDialogVisible: false,
      filters: {
        search: '',
        category: null
      },
      dashboard: '',
      selectVariableModal: false,
      selectAggregationModal: false,
      itemToAdd: null,
      chartKey: null,
      layoutReady: false,
      closeConfirmationDialogVisible: false,
      anyProgressMade: false,
      tableImg,
      diagnosticTableImg,
      players,
      defaultTableHeaders,
      defaultDiagnosticsTableHeader,
      line_chart,
      bar_chart,
      eventTemplate
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module
    },
    layoutContainsPrimaryElement() {
      return this.selectedGrid && (this.selectedGrid.some(item => item.type === 'table'))
    },
    player() {
      return this.players[0]
    },
    chartPlayers() {
      return this.players.slice(1)
    },
    filteredCharts() {
      return this.charts.data.filter(chart => this.dashboardConfiguration.charts.includes(chart.key))
    },
    filteredCategories() {
      return [{ name: 'RPE' }, { name: 'GPS' }, { name: 'HR' }, { name: 'RR' }, { name: 'WELLNESS' }].filter(
        item => this.dashboardConfiguration?.variable_categories?.includes(item.name)
      )
    },
    dashboardConfiguration() {
      return dashboardConfig[this.dashboard] ?? {}
    },
    layouts() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS].filter(layout => layout.dashboard === this.dashboard)
    },
    searchFilteredVariables() {
      return this.variables.data
        .filter(variable => this.dashboardConfiguration.variable_categories.includes(variable.category))
        .filter(variable => this.filters.search ? variable.long_text.toLowerCase().includes(this.filters.search.toLowerCase()) : true)
        .filter(variable => !this.filters.category || variable.category === this.filters.category)
        .filter(variable => this.dashboardConfiguration.selectAggregation ? !this.dashboardConfiguration.variablesExcluded.includes(variable.value) : true)
    },
    aggregations() {
      return [
        { value: 'DL', text: this.$t('variables.DL.long') },
        { value: 'WL', text: this.$t('variables.WL.long') },
        { value: 'WC', text: this.$t('variables.WC.long') },
        { value: 'AL', text: this.$t('variables.AL.long') },
        { value: 'CL', text: this.$t('variables.CL.long') },
        { value: 'ACWR', text: this.$t('variables.ACWR.long') },
        { value: 'FRES', text: this.$t('variables.FRES.long') },
        { value: 'MON', text: this.$t('variables.MON.long') },
        { value: 'STR', text: this.$t('variables.STR.long') }
      ]
    }
  },
  watch: {
    selectVariableModal(val) {
      if (!val) {
        this.filters = {
          search: '',
          category: null
        }
      }
    }
  },
  created() {
    EventBus.$on(this.$events.ADD_LAYOUT, (e) => {
      this.dashboard = e.dashboard
      this.modalVisible = true

      this.fetchVariables()
      this.fetchCharts()

      this.selectedLayout = {
        id: null,
        name: '',
        grid: [],
        variables: []
      }
      this.selectedGrid = []
      this.layoutReady = false

      this.$nextTick(() => {
        if (this.$refs.layoutContainer) this.$refs.layoutContainer.scrollTop = 0
        this.anyProgressMade = false
      })
    })

    EventBus.$on(this.$events.EDIT_LAYOUT, (e) => {
      this.dashboard = e.dashboard
      this.modalVisible = true

      this.fetchVariables()
      this.fetchCharts()

      this.selectedLayout = cloneDeep(e.selectedLayout)
      this.selectedGrid = cloneDeep(e.selectedLayout.grid)
      this.layoutReady = false

      this.$nextTick(() => {
        if (this.$refs.layoutContainer) this.$refs.layoutContainer.scrollTop = 0
        this.anyProgressMade = false
      })
    })
  },
  methods: {
    getColorForValue,
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        this.selectedLayout.grid = this.selectedGrid

        let response = null
        if (this.selectedLayout.id) {
          response = await LayoutApiService.updateLayout({
            id: this.selectedLayout.id,
            name: this.selectedLayout.name,
            grid: this.selectedLayout.grid,
            variables: this.selectedLayout.variables.map(variable => {
              return {
                id: variable.id,
                order: variable.order
              }
            })
          })
        } else {
          response = await LayoutApiService.createLayout({
            name: this.selectedLayout.name,
            grid: this.selectedLayout.grid,
            dashboard: this.dashboard,
            order: this.layouts.length + 1
          })
        }

        this.selectedLayout = cloneDeep(response.data)
        this.layoutReady = false

        EventBus.$emit(this.$events.LAYOUT_UPDATED, response.data)
        this.$store.dispatch(this.$storeActions.APP_FETCH_LAYOUTS)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_attach.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteLayout() {
      if (!this.selectedLayout.id) {
        this.deleteConfirmationDialogVisible = false
        return
      }

      try {
        this.deleting = true

        await LayoutApiService.deleteLayout(this.selectedLayout.id)

        this.modalVisible = false

        this.$store.dispatch(this.$storeActions.APP_FETCH_LAYOUTS)
        EventBus.$emit(this.$events.LAYOUT_DELETED, this.selectedLayout)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_attach.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
        this.deleteConfirmationDialogVisible = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        if (!this.$store.getters[this.$storeGetters.VARIABLES_ALL]) {
          await this.$store.dispatch(this.$storeActions.VARIABLES_FETCH_ALL)
        }

        this.variables.data = this.$store.getters[this.$storeGetters.VARIABLES_ALL]
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    async fetchCharts() {
      try {
        this.charts.loading = true

        const response = await ChartApiService.getCharts()

        this.charts.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_fetch.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.charts.loading = false
      }
    },
    addVariableToLayout(variable, width, height) {
      if (!this.selectedGrid) return

      let maxY = 0
      let x = 0

      if (this.selectedGrid.length === 0) {
        this.selectedGrid.push({
          i: '1',
          x: 0,
          y: 0,
          w: width,
          h: height,
          type: 'card',
          key: variable.value
        })
        return
      }

      for (const item of this.selectedGrid) {
        if (item.y + item.h > maxY) {
          maxY = item.y + item.h
        }
      }

      for (const item of this.selectedGrid) {
        if (item.y + item.h === maxY && item.x + item.w <= 12) {
          x = item.x + item.w
        }
      }

      const lastItemId = parseInt(this.selectedGrid[this.selectedGrid.length - 1].i)

      this.selectedGrid.push({
        i: `${lastItemId + 1}`,
        x: x + width > 12 ? 0 : x,
        y: x + width > 12 ? maxY : maxY - height,
        w: width,
        h: height,
        type: 'card',
        key: variable.value
      })

      this.selectVariableModal = false
      this.$nextTick(() => {
        this.$refs.layoutContainer.scrollTop = this.$refs.layoutContainer.scrollHeight
        this.anyProgressMade = true
      })
    },
    addTableToLayout(type) {
      if (!this.selectedGrid) return

      const maxY = this.selectedGrid.reduce((acc, item) => {
        return item.y + item.h > acc ? item.y + item.h : acc
      }, 0)

      const lastItemId = this.selectedGrid.length > 0 ? parseInt(this.selectedGrid[this.selectedGrid.length - 1].i) : 0

      this.selectedGrid.push({
        i: `${lastItemId + 1}`,
        x: 0,
        y: maxY,
        w: 12,
        h: 6,
        type: type.toLowerCase(),
        key: type.toUpperCase()
      })

      this.$nextTick(() => {
        this.$refs.layoutContainer.scrollTop = this.$refs.layoutContainer.scrollHeight
        this.anyProgressMade = true
      })
    },
    addChartToLayout(chart) {
      if (!this.selectedGrid) return

      let maxY = 0
      let x = 0

      chart.type = 'chart'

      const width = this.isSmallChart(chart) ? 4 : 12
      const height = this.isSmallChart(chart) ? 4 : chart.key !== 'LOAD_OVERVIEW' ? 6 : 8

      if (this.selectedGrid.length === 0) {
        this.selectedGrid.push({
          i: '1',
          x: 0,
          y: 0,
          w: width,
          h: height,
          type: 'chart',
          key: chart.key,
          variable: chart.variable,
          aggregation: chart.aggregation
        })
        return
      }

      for (const item of this.selectedGrid) {
        if (item.y + item.h > maxY) {
          maxY = item.y + item.h
        }
      }

      for (const item of this.selectedGrid) {
        if (item.y + item.h === maxY && item.x + item.w <= 12) {
          x = item.x + item.w
        }
      }

      const lastItemId = parseInt(this.selectedGrid[this.selectedGrid.length - 1].i)

      this.selectedGrid.push({
        i: `${lastItemId + 1}`,
        x: x + width > 12 ? 0 : x,
        y: x + width > 12 ? maxY : maxY - height,
        w: width,
        h: height,
        type: 'chart',
        key: chart.key,
        variable: chart.variable,
        aggregation: chart.aggregation
      })

      this.layoutReady = false

      this.$nextTick(() => {
        this.$refs.layoutContainer.scrollTop = this.$refs.layoutContainer.scrollHeight
        this.anyProgressMade = true
      })
    },
    addSimpleChartToLayout(chart, width, height, type) {
      if (!this.selectedGrid) return

      let maxY = 0
      let x = 0

      if (this.selectedGrid.length === 0) {
        this.selectedGrid.push({
          i: '1',
          x: 0,
          y: 0,
          w: width,
          h: height,
          type: type + '_chart',
          key: chart.value
        })
        return
      }

      for (const item of this.selectedGrid) {
        if (item.y + item.h > maxY) {
          maxY = item.y + item.h
        }
      }

      for (const item of this.selectedGrid) {
        if (item.y + item.h === maxY && item.x + item.w <= 12) {
          x = item.x + item.w
        }
      }

      const lastItemId = parseInt(this.selectedGrid[this.selectedGrid.length - 1].i)

      this.selectedGrid.push({
        i: `${lastItemId + 1}`,
        x: x + width > 12 ? 0 : x,
        y: x + width > 12 ? maxY : maxY - height,
        w: width,
        h: height,
        type: type + '_chart',
        key: chart.value
      })

      this.layoutReady = false

      this.$nextTick(() => {
        this.$refs.layoutContainer.scrollTop = this.$refs.layoutContainer.scrollHeight
        this.anyProgressMade = true
      })
    },
    addItemToLayout(item) {
      if (this.itemToAdd === 'card') {
        this.addVariableToLayout(item, 3, 2)
      } else if (this.itemToAdd === 'line_chart') {
        this.addSimpleChartToLayout(item, 12, 6, 'line')
      } else if (this.itemToAdd === 'bar_chart') {
        this.addSimpleChartToLayout(item, 12, 6, 'bar')
      } else if (this.itemToAdd === 'chart') {
        this.addChartToLayout({
          key: this.chartKey,
          type: 'chart',
          variable: item.value,
          aggregation: item.aggregation
        })
      }
      this.itemToAdd = null
      this.chartKey = null
      this.selectVariableModal = false
      this.selectAggregationModal = false
    },
    handleChartClick(chart) {
      const itlChartsNeedingVariable = ['LOAD_OVERVIEW', 'WEEK_CHANGE', 'FRESHNESS']

      if (this.dashboardConfiguration.selectChartAggregation && !this.dashboardConfiguration.chartWithoutAggregations.includes(chart.key)) {
        this.selectAggregationModal = true
        this.chartKey = chart.key
        this.itemToAdd = 'chart'
        return
      } else if (itlChartsNeedingVariable.includes(chart.key)) {
        this.itemToAdd = 'chart'
        this.chartKey = chart.key
        this.selectVariableModal = true
        return
      } else if (this.dashboardConfiguration.selectAggregation && !this.itemToAdd && this.chartKey !== 'TRAINING_LOAD' && !this.dashboardConfiguration.chartWithoutAggregations.includes(chart.key)) {
        this.itemToAdd = 'chart'
        this.chartKey = chart.key
        this.selectVariableModal = true
        return
      }
      this.addChartToLayout(chart)
    },
    removeItem(item) {
      this.selectedGrid = this.selectedGrid.filter(i => i.i !== item.i)
      this.anyProgressMade = true
    },
    getVariableName(value) {
      if (value.indexOf('_') === -1) {
        return this.variables.data.find(variable => variable.value === value)?.long_text
      } else {
        return getVariableNameWithAggregation(value)
      }
    },
    layoutUpdatedEvent() {
      setTimeout(() => {
        this.layoutReady = true
      }, 200)
    },
    handleClose() {
      if (this.anyProgressMade) {
        this.closeConfirmationDialogVisible = true
      } else {
        this.modalVisible = false
        this.layoutReady = false
      }
    },
    isSmallChart(item) {
      if (item.type !== 'chart') return false

      if (!this.charts.data) return false

      const chartData = this.charts.data.find(chart => chart.key === item.key)
      if (!chartData) return false

      return chartData.is_individual && !this.dashboardConfiguration.smallChartExceptions.includes(chartData.key)
    },

    getMinimumHeight(item) {
      if (item.type === 'chart' || item.type === 'diagnostic_table' || item.type === 'table' || item.type === 'line_chart') {
        if (this.isSmallChart(item)) {
          return 4
        }
        return 6
      } else {
        return 2
      }
    },

    getMinimumWidth(item) {
      if (item.type === 'table' || item.type === 'diagnostic_table') {
        return 12
      } else if (item.type === 'chart' || item.type === 'line_chart') {
        if (this.isSmallChart(item)) {
          return 4
        }
        return 6
      } else {
        return 3
      }
    },

    getMaximumWidth(item) {
      if (item.type === 'chart' || item.type === 'diagnostic_table' || item.type === 'table' || item.type === 'line_chart') {
        if (this.isSmallChart(item)) {
          return 6
        }
        return 12
      } else {
        return 6
      }
    },

    getMaximumHeight(item) {
      if (item.type === 'chart' || item.type === 'diagnostic_table' || item.type === 'table' || item.type === 'line_chart') {
        if (this.isSmallChart(item)) {
          return 6
        }
        return 8
      } else {
        return 4
      }
    }
  }
}

</script>

<style scoped>

</style>
